import { useQuery, type QueryClient } from "@tanstack/react-query";
import { COMPUTE_RESOURCE_LIST_POLL_INTERVAL_MS } from "constants/computeResources";
import {
  adminListOrganizationsForUser,
  getAdminOrganization,
  getAdminOrganizationOwner,
  getOrganization,
  listOrganizationComputeResources,
  listOrganizationMembers,
  organizationHasComputeResources,
  listAdminOrganizations,
  type ListAdminOrganizationsInput,
  listAdminOrganizationsPage,
  type ListAdminOrganizationsPageInput,
  type OrganizationComputeResourceFilters,
} from "./api";

const organizationQueryStaleTimeMs = 5 * 60 * 1000;

export const organizationKeys = {
  all: ["devplane", "organizations"] as const,
  lists: () => [...organizationKeys.all, "list"] as const,
  details: () => [...organizationKeys.all, "detail"] as const,
  detail: (organizationId: string) =>
    [...organizationKeys.details(), organizationId] as const,
  currentUserList: (userId: string) =>
    [...organizationKeys.lists(), "current-user", userId] as const,
  computeResources: (
    organizationId: string,
    filters: OrganizationComputeResourceFilters,
    excludeConnectivityInfo: boolean = true,
  ) =>
    [
      ...organizationKeys.detail(organizationId),
      "compute-resources",
      filters,
      excludeConnectivityInfo ? "without-connectivity" : "with-connectivity",
    ] as const,
  computeResourceExistence: (organizationId: string) =>
    [
      ...organizationKeys.detail(organizationId),
      "compute-resource-existence",
    ] as const,
  members: (organizationId: string) =>
    [...organizationKeys.detail(organizationId), "members"] as const,
  userList: (userId: string) =>
    [...organizationKeys.lists(), "user", userId] as const,
  admin: () => [...organizationKeys.all, "admin"] as const,
  adminLists: () => [...organizationKeys.admin(), "list"] as const,
  adminList: (input: ListAdminOrganizationsInput) =>
    [...organizationKeys.adminLists(), input] as const,
  adminPages: () => [...organizationKeys.admin(), "page"] as const,
  adminPage: (input: ListAdminOrganizationsPageInput) =>
    [...organizationKeys.adminPages(), input] as const,
  adminDetails: () => [...organizationKeys.admin(), "detail"] as const,
  adminDetail: (organizationId: string) =>
    [...organizationKeys.adminDetails(), organizationId] as const,
  adminOwners: () => [...organizationKeys.admin(), "owner"] as const,
  adminOwner: (organizationId: string) =>
    [...organizationKeys.adminOwners(), organizationId] as const,
};

function useOrganizationComputeResourcesQueryWithOptions(
  organizationId: string,
  filters: OrganizationComputeResourceFilters,
  enabled: boolean,
  excludeConnectivityInfo: boolean,
) {
  return useQuery({
    queryKey: organizationKeys.computeResources(
      organizationId,
      filters,
      excludeConnectivityInfo,
    ),
    queryFn: ({ signal }) =>
      listOrganizationComputeResources(
        organizationId,
        filters,
        { signal },
        excludeConnectivityInfo,
      ),
    enabled: enabled && Boolean(organizationId),
    staleTime: COMPUTE_RESOURCE_LIST_POLL_INTERVAL_MS,
    refetchInterval: COMPUTE_RESOURCE_LIST_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

export function useOrganizationComputeResourcesQuery(
  organizationId: string,
  filters: OrganizationComputeResourceFilters = {},
  enabled: boolean = true,
) {
  return useOrganizationComputeResourcesQueryWithOptions(
    organizationId,
    filters,
    enabled,
    true,
  );
}

export function useOrganizationComputeResourcesWithConnectivityQuery(
  organizationId: string,
  filters: OrganizationComputeResourceFilters = {},
  enabled: boolean = true,
) {
  return useOrganizationComputeResourcesQueryWithOptions(
    organizationId,
    filters,
    enabled,
    false,
  );
}

export function useOrganizationHasComputeResourcesQuery(
  organizationId: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: organizationKeys.computeResourceExistence(organizationId),
    queryFn: ({ signal }) =>
      organizationHasComputeResources(organizationId, { signal }),
    enabled: enabled && Boolean(organizationId),
    staleTime: Infinity,
  });
}

export function useOrganizationMembersQuery(
  organizationId: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: organizationKeys.members(organizationId),
    queryFn: ({ signal }) =>
      listOrganizationMembers(organizationId, { signal }),
    enabled: enabled && Boolean(organizationId),
    staleTime: organizationQueryStaleTimeMs,
  });
}

export function organizationDetailQueryOptions(organizationId: string) {
  return {
    queryKey: organizationKeys.detail(organizationId),
    queryFn: () => getOrganization(organizationId),
    staleTime: organizationQueryStaleTimeMs,
  };
}

export function useListOrganizationsQuery(
  userId: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: organizationKeys.currentUserList(userId),
    queryFn: () => adminListOrganizationsForUser(userId),
    enabled: enabled && Boolean(userId),
    staleTime: organizationQueryStaleTimeMs,
  });
}

export function useAdminListOrganizationsForUserQuery(userId: string) {
  return useQuery({
    queryKey: organizationKeys.userList(userId),
    queryFn: () => adminListOrganizationsForUser(userId),
    enabled: Boolean(userId),
    staleTime: organizationQueryStaleTimeMs,
  });
}

export function useGetOrganizationQuery(
  organizationId: string,
  enabled: boolean = true,
) {
  return useQuery({
    ...organizationDetailQueryOptions(organizationId),
    enabled: enabled && Boolean(organizationId),
  });
}

export function adminOrganizationQueryOptions(organizationId: string) {
  return {
    queryKey: organizationKeys.adminDetail(organizationId),
    queryFn: () => getAdminOrganization(organizationId),
    staleTime: organizationQueryStaleTimeMs,
  };
}

export function useAdminOrganizationQuery(organizationId: string) {
  return useQuery({
    ...adminOrganizationQueryOptions(organizationId),
    enabled: Boolean(organizationId),
  });
}

export function useAdminOrganizationsQuery(
  input: ListAdminOrganizationsInput = {},
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: organizationKeys.adminList(input),
    queryFn: () => listAdminOrganizations(input),
    enabled,
    staleTime: organizationQueryStaleTimeMs,
  });
}

export function adminOrganizationsPageQueryOptions(
  input: ListAdminOrganizationsPageInput,
) {
  return {
    queryKey: organizationKeys.adminPage(input),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      listAdminOrganizationsPage(input, { signal }),
    staleTime: organizationQueryStaleTimeMs,
  };
}

export function useAdminOrganizationsPageQuery(
  input: ListAdminOrganizationsPageInput,
  enabled: boolean = true,
) {
  return useQuery({
    ...adminOrganizationsPageQueryOptions(input),
    enabled,
  });
}

export function adminOrganizationOwnerQueryOptions(organizationId: string) {
  return {
    queryKey: organizationKeys.adminOwner(organizationId),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getAdminOrganizationOwner(organizationId, { signal }),
    staleTime: organizationQueryStaleTimeMs,
  };
}

export function useAdminOrganizationOwnerQuery(
  organizationId: string,
  enabled: boolean = true,
) {
  return useQuery({
    ...adminOrganizationOwnerQueryOptions(organizationId),
    enabled: enabled && Boolean(organizationId),
  });
}

export async function invalidateOrganizationQueries(queryClient: QueryClient) {
  await queryClient.invalidateQueries({
    queryKey: organizationKeys.all,
  });
}
